import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Info } from 'lucide-react';
import { BoardView } from '../BoardView';
import { LodgeApproveNeeded } from '../PremiumBoard';
import { ActivityEntryPanel } from '../ActivityEntryPanel';
import PipelineHealth from '../jarvis/PipelineHealth';
import { Periods, Feed } from '../jarvis/PeriodsFeed';
import { Bars, SettlementPanels, WeeklyGamePlanTable } from '../jarvis/PerformanceTab';
import { ForecastHero, PathToTarget, DecideList, NextMonth, Outlook } from '../jarvis/ForecastTab';
import VerdictHero from './VerdictHero';
import TargetsGrid from './TargetsGrid';
import LiftPanel from './LiftPanel';
import AttentionPanel from './AttentionPanel';
import TrendsPanel from './TrendsPanel';
import { Section } from './shared';
import '../jarvis/forecast.css';
import './command.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SECTIONS = [
  ['verdict', 'Verdict'], ['targets', 'Targets'], ['board', 'Pipeline Board'], ['lift', 'Lift our game'],
  ['trends', 'Trends'], ['forecast', 'Forecast'], ['health', 'Pipeline health'], ['performance', 'Performance'], ['activity', 'Activity'],
];

function SectionNav() {
  const [on, setOn] = useState('verdict');
  useEffect(() => {
    const els = SECTIONS.map(([id]) => document.getElementById(id)).filter(Boolean);
    const io = new IntersectionObserver((es) => { es.forEach((e) => { if (e.isIntersecting) setOn(e.target.id); }); }, { rootMargin: '-25% 0px -65% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (
    <nav className="gpc-nav" aria-label="Sections" data-testid="gpc-nav">
      {SECTIONS.map(([id, label]) => (
        <button type="button" key={id} className={on === id ? 'on' : ''} onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })} data-testid={`gpc-nav-${id}`}>{label}</button>
      ))}
    </nav>
  );
}

/* Executive Command Centre — the Overview tab. Weekly Pipeline Board is embedded unchanged. */
export default function CommandCentre({ month, boardData, live, readOnly, options, authToken, brokerFilter, sourceFilter, refreshSignal, onRefresh, onOpenFile, onEditDeal, onStage }) {
  const [cc, setCc] = useState(null);
  const [fc, setFc] = useState(null);
  const [err, setErr] = useState(false);
  const first = useRef(true);

  const load = useCallback(async () => {
    if (!authToken || !month) return;
    const params = new URLSearchParams({ month });
    if (brokerFilter) params.append('broker', brokerFilter);
    if (sourceFilter) params.append('lead_source', sourceFilter);
    const h = { headers: { Authorization: `Bearer ${authToken}` } };
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API_BASE}/api/gameplan/command-centre?${params}`, h),
        axios.get(`${API_BASE}/api/gameplan/forecast?${params}`, h).catch(() => null),
      ]);
      setCc(a.data); if (b) setFc(b.data); setErr(false);
    } catch (e) { setErr(true); }
    first.current = false;
  }, [authToken, month, brokerFilter, sourceFilter]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  if (!cc && err) return <div className="gpj-panel" data-testid="gpc-error"><div className="gpf-callout amber"><Info className="w-4 h-4" /><span>Couldn't load the command centre. Try refreshing.</span></div></div>;
  if (!cc) return <div className="gpj-grid" data-testid="gpc-loading"><div className="gpc-skel" /><div className="gpc-skel" style={{ height: 320 }} /></div>;

  const target = Number(boardData?.monthly_target_amount || 0);
  const bd = live?.business_days || {};
  const dailyShare = cc.lodgements?.month?.target && bd.total ? cc.lodgements.month.target / bd.total : (target && bd.total ? target / bd.total : 0);

  return (
    <div className="gpj-grid gpc-root" data-testid="gpc-root">
      <SectionNav />

      <Section id="verdict" label="Executive verdict" hint="live system data · projections labelled">
        <VerdictHero cc={cc} live={live} />
      </Section>

      <Section id="targets" label="Targets vs actual" hint="this week and this month">
        <TargetsGrid cc={cc} />
      </Section>

      <Section id="board" label="Weekly Pipeline Board" hint="files by week of lodgement">
        <BoardView month={month} boardData={boardData} live={live} readOnly={readOnly} onUpdateDeal={onRefresh} onRefresh={onRefresh} options={options} authToken={authToken} onOpenFile={onOpenFile} onEditDeal={onEditDeal} onStage={onStage} boardOnly />
      </Section>

      <Section id="lift" label="Where we need to lift our game" hint="ranked by impact on target">
        <div className="gpc-three">
          <LiftPanel cc={cc} />
          <AttentionPanel live={live} cc={cc} onOpenFile={onOpenFile} onStage={onStage} />
        </div>
      </Section>

      <Section id="trends" label="Trends" hint="improving or declining · like-for-like">
        <TrendsPanel cc={cc} />
      </Section>

      <Section id="forecast" label="Month-end settlement forecast" hint="probability-weighted from the live pipeline · a forecast, not a result">
        {fc ? (
          <div className="gpj-grid" data-testid="gpc-forecast">
            <ForecastHero d={fc} />
            <PathToTarget d={fc} />
            <div className="gpj-grid gpj-two">
              <DecideList d={fc} />
              <NextMonth d={fc} />
            </div>
            <Outlook d={fc} />
          </div>
        ) : <div className="gpj-panel"><div className="gpj-empty">Forecast unavailable right now.</div></div>}
      </Section>

      <Section id="health" label="Pipeline health" hint="stage limits from Set Goals">
        <div className="gpj-ov-pair">
          <PipelineHealth live={live} onStage={onStage} />
          <LodgeApproveNeeded authToken={authToken} refreshSignal={refreshSignal} month={month} />
        </div>
      </Section>

      <Section id="performance" label="Performance detail" hint="daily lodgements · settlements · weekly plan">
        <div className="gpj-grid">
          <section className="gpj-panel">
            <div className="gpj-panel-h"><h2>Daily lodgements this month</h2><p>Each bar is one business day, against the daily share of the lodgement target</p></div>
            <Bars refValue={dailyShare} testid="gpj-daily-bars" items={(live?.daily_lodged || []).map((d) => ({ l: String(d.day), v: d.amount, dim: d.future, c: d.weekend ? '#64d2ff' : '#0a84ff' }))} />
          </section>
          <SettlementPanels live={live} />
          <WeeklyGamePlanTable live={live} boardData={boardData} authToken={authToken} month={month} />
        </div>
      </Section>

      <Section id="activity" label="Activity" hint="daily entries · today, this week, this month · live feed">
        <div className="gpj-grid">
          <section className="gpj-panel" data-testid="gp-daily-activity">
            <ActivityEntryPanel authToken={authToken} month={month} refreshSignal={refreshSignal} onSaved={onRefresh} bare />
          </section>
          <div className="gpj-grid gpj-two">
            <Periods live={live} />
            <Feed live={live} />
          </div>
        </div>
      </Section>
    </div>
  );
}
